/**
 * Internationalisation.
 *
 * English only for now, but every user-facing string goes through `t()` from the
 * first screen. The brief asks for a structure that allows additional South
 * African languages later, and "later" is exactly when retrofitting this becomes
 * a week of touching every component instead of adding a JSON file.
 *
 * Locale is `en-ZA`: the copy is South African English ("R", "spaza", DD/MM).
 */

import i18next from "i18next";
import { initReactI18next } from "react-i18next";

export const SUPPORTED_LANGUAGES = [
  { code: "en-ZA", label: "English" },
  // Phase 11: isiZulu, Sesotho, Afrikaans. Adding one is a resource file plus a
  // line here — no component changes.
] as const;

const en = {
  brand: {
    // NOTE: the two supplied screenshots disagree — the Sales mock reads
    // "Shop Local. Support Local. Scale Africa." and the Inventory mock reads
    // "Powering Township Commerce." Flagged as open decision §8.5; using the
    // shorter one until confirmed, because it fits the 260px rail without
    // wrapping.
    tagline: "Powering Township Commerce",
  },

  nav: {
    primary: "Main navigation",
    dashboard: "Dashboard",
    sales: "Sales",
    customers: "Customers",
    inventory: "Inventory",
    suppliers: "Suppliers",
    procurement: "Procurement",
    reports: "Reports",
    expenses: "Expenses",
    settings: "Settings",
    support: "Support",
    logout: "Logout",
  },

  topbar: {
    notifications: "Notifications",
    noBranch: "No branch selected",
    branchPending: "Branch and section selection arrives in Phase 1",
    signedOut: "Not signed in",
    noRole: "Sign-in arrives in Phase 1",
  },

  sync: {
    online: "Online",
    offline: "Offline",
    syncing: "Syncing",
    failed: "Sync failed",
    checking: "Checking",
    lastSynced: "Last synced {{when}}",
    pendingCount_one: "{{count}} transaction waiting to upload",
    pendingCount_other: "{{count}} transactions waiting to upload",
  },

  summary: {
    today: "Today's Summary",
    sales: "Sales",
    transactions: "Transactions",
    averageSale: "Average Sale",
    stockOverview: "Stock Overview",
    totalProducts: "Total Products",
    totalStockValue: "Total Stock Value",
    lowStockItems: "Low Stock Items",
    outOfStockItems: "Out of Stock Items",
    availableIn: "Figures appear once {{phase}} is delivered.",
  },

  placeholder: {
    title: "{{screen}} is not built yet",
    scheduled: "Scheduled for {{phase}}.",
  },

  notFound: {
    title: "That page does not exist",
    description: "The link may be out of date, or the screen may not be built yet.",
    action: "Go to the dashboard",
  },

  common: {
    retry: "Try again",
    cancel: "Cancel",
    save: "Save",
    search: "Search",
  },
} as const;

void i18next.use(initReactI18next).init({
  resources: { "en-ZA": { translation: en } },
  lng: "en-ZA",
  fallbackLng: "en-ZA",
  defaultNS: "translation",
  interpolation: {
    // React escapes for us; double-escaping mangles an apostrophe in
    // "Today's Summary".
    escapeValue: false,
  },
  // A missing key should be visible in development and harmless in production:
  // returning the key itself makes the gap obvious without breaking the screen.
  returnNull: false,
});

export default i18next;
